import { useRequiredContext } from "@redotech/react-util/context";
import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { useAskRedoEnabled } from "@redotech/redo-merchant-app-common/ask-redo/use-ask-redo-enabled";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  globalSearchCategory,
  GlobalSearchCategory,
} from "@redotech/redo-model/global-search/global-search-category";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import {
  CommandPaletteFilter,
  CommandPaletteProvider,
} from "@redotech/redo-web/command-palette/contexts/command-palette-context";
import { memo, useCallback } from "react";
import { useSearchHistory } from "../side-navigation/use-search-history";
import { useSearchItems } from "../side-navigation/use-search-items";

interface GlobalSearchProps {
  children: React.ReactNode;
}

export const GlobalSearch = memo(function GlobalSearch({
  children,
}: GlobalSearchProps) {
  const { searchHistory, saveToSearchHistory } = useSearchHistory();
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const legacyAiPanel = useAiSlidePanel();
  const askRedoPanel = useAskRedoSlidePanel();
  const { enabled: askRedoEnabled, pending: askRedoPending } =
    useAskRedoEnabled();
  const onSearch = useSearchItems(merchantRpcClient);

  // Route "Ask AI" to whichever panel is currently the active
  // surface for this team. Without the flag check the command
  // palette would keep opening the legacy chat after a team flips
  // to Ask Redo. Drop the action while the flag is still resolving
  // — opening the wrong panel mid-flag-load is worse UX than the
  // command palette no-oping for the ~200ms it takes to resolve.
  const onAskAI = useCallback(
    (searchValue: string) => {
      if (askRedoPending) {
        return;
      }
      if (askRedoEnabled) {
        askRedoPanel.open({
          kind: "prefill",
          input: searchValue,
          autoSubmit: true,
        });
      } else {
        legacyAiPanel.open({ prefillInput: searchValue, autoSubmit: true });
      }
    },
    [askRedoPending, askRedoEnabled, askRedoPanel, legacyAiPanel],
  );

  const onAction = useCallback(
    (search: string) => {
      saveToSearchHistory(search);
    },
    [saveToSearchHistory],
  );

  return (
    <CommandPaletteProvider
      filters={filters}
      initialResults={searchHistory}
      onAction={onAction}
      onAskAI={onAskAI}
      onSearch={onSearch}
    >
      {children}
    </CommandPaletteProvider>
  );
});

const globalSearchCategoryToFriendly: Record<GlobalSearchCategory, string> = {
  conversations: "Conversations",
  orders: "Orders",
  returns: "Returns",
  customers: "Customers",
  pages: "Pages",
  documentation: "Help Articles",
};

const filters: CommandPaletteFilter[] = globalSearchCategory.map(
  (category): CommandPaletteFilter => ({
    id: category,
    label: globalSearchCategoryToFriendly[category],
    enabled: false,
    visible: true,
  }),
);
